/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { View, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { isWeb, isAndroid, isIos, fullWidth } from '@global/screens';
import { convertPromotion, parseDate } from '@global/helpers';
import Colors from '@global/colors';
import { ARROW_SIZE } from '@/constants';
import Image from '@components/global/Image';
import ImageContainV2 from '@components/global/ImageContainV2';
import TitleSubSection from '@components/global/TitleSubSection';
import SociollaText from '@components/sociolla/SociollaText';
import ModalHalf from '@components/modal/ModalHalf';
import ProductTab from '@/screens/catalog/components/ProductTab';
import * as Layout from '@components/screen/Layout';

import useApi from '@/hooks/useApi';
import { MsCatalog } from '@api/ms-catalog';
import { paramsReseted } from '@/store/params';
import { showPopup } from '@/store/modal';
import { downloadFile } from '@global/helpers';
import Toast from 'react-native-toast-message';

const BANNER_SIZE = {
  width: isWeb ? 350 : fullWidth,
  height: 140,
};

const CatalogPromoScreen = (props) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [showDetailData, setShowDetailData] = useState(null);
  const [detailData, setDetailData] = useState({});
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setDetailData({});
      setSections([]);
      dispatch(paramsReseted());
      if (props?.route?.params?.id) {
        getDetail(props.route.params.id);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  const getCMSDetail = useApi(MsCatalog.getCMSDetail);

  const getSections = async (id) => {
    setLoading(true);
    try {
      let result = [];
      try {
        const textImages = await MsCatalog.getCMSSectionDetail(
          id,
          'text-images',
        );
        if (textImages?.data?.data?.length) {
          result = result.concat(textImages.data.data);
        }
      } catch (error) {}
      try {
        const products = await MsCatalog.getCMSSectionDetail(id, 'products');
        if (products?.data?.data?.length) {
          result = result.concat({
            type: 'products',
            products: products.data.data,
          });
        }
      } catch (error) {}
      try {
        const pdf_documents = await MsCatalog.getCMSSectionDetail(
          id,
          'pdf_documents',
        );
        if (pdf_documents?.data?.data?.length) {
          result = result.concat(pdf_documents.data.data);
        }
      } catch (error) {}
      try {
        const promotions = await MsCatalog.getCMSSectionDetail(
          id,
          'promotions',
        );
        if (promotions?.data?.data?.length) {
          result = result.concat({
            type: 'promotions',
            promotions: convertPromotion(promotions.data.data),
          });
        }
      } catch (error) {}
      setSections(result);
      setLoading(false);
    } catch (error) {
      console.log('error', error);
      setSections([]);
      setLoading(false);
    }
  };

  const getDetail = async (id) => {
    setLoading(true);
    try {
      const response = await getCMSDetail.request(id);
      if (response?.data?.data && Object.keys(response.data.data).length) {
        const result = response.data.data;
        result.id = result._id;
        getSections(result._id);
        setDetailData(result);
      } else {
        setSections([]);
        setDetailData({});
      }
      setLoading(false);
    } catch (e) {
      console.log('e', e);
      setLoading(false);
    }
  };

  const onOpen = (item) => {
    const newTab = [...activeTab];
    const idx = activeTab?.indexOf(item?._id);
    if (idx > -1) {
      newTab.splice(idx, 1);
    } else {
      newTab.push(item._id);
    }
    setActiveTab(newTab);
  };

  const onOpenDetailBanner = (data) => {
    setShowDetailData(data);
    setShowDetail(true);
  };

  const onCloseDetailBanner = () => {
    setShowDetailData(null);
    setShowDetail(false);
  };

  const showPromotionPeriod = useMemo(() => {
    let result = '-';

    if (detailData.promo_date) {
      let start = '';
      let end = '';
      if (detailData.promo_date?.from) {
        start = parseDate(detailData.promo_date.from);
      }
      if (detailData.promo_date?.to) {
        end = parseDate(detailData.promo_date.to);
      }
      result = `${start} - ${end}`;
    }

    return result;
  }, [detailData]);

  const promoDescription = sections?.find((s) => s.type == 'text')?.description;

  const openPDF = async (id) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    if (id) {
      downloadFile(id, 'pdf-section', 'pdf', true);
      try {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({ text1: 'PDF Files Downloading', type: 'success' });
      } catch (err) {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      }
    } else {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({ text1: 'A system error occurred', type: 'error' });
    }
  };

  const generatePdfName = (index) => {
    return `pdf-files-${index + 1}.pdf`;
  };

  return (
    <Layout.Container>
      {isWeb ? (
        <Layout.Back
          title="Product Catalog"
          onCallback={() => {
            props.navigation.navigate('CatalogScreen', { tab: 'promo' });
          }}
        />
      ) : (
        false
      )}

      <Layout.Wrapper rounded fullWidth={Boolean(activeTab?.length)}>
        {loading ? (
          <Layout.Loader />
        ) : (
          <Layout.ScrollViewRefresher
            onCallback={() => getDetail(props?.route?.params?.id)}>
            {detailData?.banner_desktop ? (
              <ImageContainV2
                source={detailData.banner_desktop}
                containerStyle={{ width: BANNER_SIZE.width }}
                style={{
                  ...BANNER_SIZE,
                  marginBottom: 15,
                  marginLeft: isWeb ? 15 : 0,
                  marginTop: isWeb ? 15 : 0,
                }}
              />
            ) : (
              false
            )}
            <View style={styles.detailWrapper}>
              <SociollaText type="bold" style={styles.title}>
                {detailData?.title ? detailData.title : '-'}
              </SociollaText>
              {detailData?.page_type == 'promo' ? (
                <SociollaText style={styles.date}>
                  {showPromotionPeriod}
                </SociollaText>
              ) : (
                false
              )}
              <SociollaText style={styles.desc}>
                {promoDescription ? promoDescription : false}
              </SociollaText>
            </View>
            {sections?.length ? (
              <>
                {sections.map((item, idx) => (
                  <View key={idx}>
                    {item.promotions?.length ? (
                      <>
                        <TitleSubSection title="PROMO LIST" />
                        <View style={styles.listWrapper}>
                          {item.promotions.map((child, childIdx) => (
                            <View style={styles.tabSection} key={childIdx}>
                              <TouchableOpacity
                                activeOpacity={0.8}
                                style={styles.tabItem}
                                onPress={() => onOpen(child)}>
                                <SociollaText
                                  type="medium"
                                  style={[
                                    styles.tabText,
                                    activeTab.includes(child._id)
                                      ? styles.tabTextActive
                                      : {},
                                  ]}>
                                  {child?.promo_rule?.name
                                    ? child.promo_rule.name
                                    : '-'}
                                </SociollaText>
                                <Image
                                  style={[
                                    ARROW_SIZE,
                                    {
                                      transform: [
                                        {
                                          rotate: activeTab.includes(
                                            child?.promo_rule?.id,
                                          )
                                            ? '180deg'
                                            : '360deg',
                                        },
                                      ],
                                    },
                                  ]}
                                  width={ARROW_SIZE.width}
                                  height={ARROW_SIZE.height}
                                  svg
                                  source={require('@assets/img/icons/arrow-down-grey.svg')}
                                />
                              </TouchableOpacity>
                              {activeTab.includes(child?._id) ? (
                                <View style={{ marginTop: 30 }}>
                                  <SociollaText style={styles.desc}>
                                    {child?.promo_rule?.description
                                      ? child.promo_rule.description
                                      : '-'}
                                  </SociollaText>
                                  {/* <ProductTab {...props} /> */}
                                </View>
                              ) : (
                                false
                              )}
                            </View>
                          ))}
                        </View>
                      </>
                    ) : item.type == 'images' ? (
                      <>
                        <TitleSubSection title="FEATURED PROMOTION" />
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                          style={{ width: '100%', marginVertical: 15 }}>
                          {item.image?.length
                            ? item.image.map((item, idx) => (
                                <TouchableOpacity
                                  activeOpacity={0.8}
                                  key={idx}
                                  style={{
                                    marginLeft: idx == 0 ? 15 : 0,
                                    marginRight: 15,
                                  }}
                                  onPress={() => onOpenDetailBanner(item)}>
                                  <ImageContainV2
                                    source={item.url}
                                    style={{
                                      width: isWeb ? 210 : 190,
                                      height: isWeb ? 170 : 140,
                                    }}
                                  />
                                </TouchableOpacity>
                              ))
                            : false}
                        </ScrollView>
                      </>
                    ) : item.type == 'products' ? (
                      <>
                        <TitleSubSection title="PRODUCTS" />
                        <ProductTab id={detailData?._id} {...props} />
                      </>
                    ) : item.type == 'pdf_documents' ? (
                      <View key={idx} style={styles.mBottom}>
                        <TitleSubSection title="PDF FILES" />
                        <View
                          style={[
                            styles.pdfList,
                            styles.vPadding,
                            { flexWrap: 'wrap' },
                          ]}>
                          {item.pdf_documents.map((pdf, pdfIdx) => (
                            <TouchableOpacity
                              activeOpacity={0.6}
                              onPress={() => openPDF(pdf.url)}
                              style={styles.pdfbox}
                              key={pdfIdx}>
                              <Image
                                style={{ width: 35, height: 43 }}
                                width={35}
                                height={43}
                                source={require('@assets/img/icons/pdf-download.svg')}
                              />
                              <SociollaText style={styles.pdfTitle}>
                                {generatePdfName(pdfIdx)}
                              </SociollaText>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>
                    ) : (
                      false
                    )}
                  </View>
                ))}
              </>
            ) : (
              false
            )}
            <Layout.Spacer space={isAndroid ? 110 : isIos ? 55 : 0} />
          </Layout.ScrollViewRefresher>
        )}
        <ModalHalf
          title={'Image Detail'}
          visible={showDetail}
          onClose={() => onCloseDetailBanner()}
          maxWidth={isWeb ? 800 : '100%'}
          width={isWeb ? 800 : '100%'}>
          <View style={styles.detailModalWrapper}>
            <ImageContainV2
              source={showDetailData?.url}
              style={{
                width: isWeb ? 700 : fullWidth - 30,
                height: isWeb ? 550 : 200,
              }}
            />
            <SociollaText style={styles.modalText}>
              {showDetailData?.description ? showDetailData.description : '-'}
            </SociollaText>
          </View>
        </ModalHalf>
      </Layout.Wrapper>
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  detailWrapper: {
    padding: 15,
  },
  title: {
    fontSize: isWeb ? 22 : 18,
    marginBottom: 10,
  },
  date: {
    fontSize: isWeb ? 13 : 12,
    color: Colors.grey1,
    marginBottom: 15,
  },
  desc: {
    fontSize: isWeb ? 14 : 13,
    marginBottom: 10,
  },
  listWrapper: {
    width: '100%',
    paddingBottom: 15,
  },
  tabSection: {
    width: '100%',
    padding: 15,
    borderBottomColor: Colors.grey0,
    borderBottomWidth: 1,
  },
  tabItem: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tabText: {
    fontSize: 15,
    color: Colors.black0,
  },
  tabTextActive: {
    color: Colors.blue0,
  },
  detailModalWrapper: {
    width: '100%',
    paddingHorizontal: 15,
    paddingBottom: 15,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalText: {
    marginTop: 15,
  },
  pdfList: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  pdfbox: {
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.blue0,
    padding: 8,
    marginLeft: 16,
    marginVertical: 16,
  },
  pdfTitle: { marginTop: 10 },
});

export default CatalogPromoScreen;
